/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ViewBusinessEventRegistrationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import styles from "./index.module.css";

import { ReactComponent as CalendarIcon } from "../../assets/icons/ViewRegistrationModal/CalendarIcon.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/ViewRegistrationModal/ClockIcon.svg";
import { ReactComponent as UserNameIcon } from "../../assets/icons/UserViews/UserNameIcon.svg";
import CloseIcon from "@mui/icons-material/Close";

const ViewBusinessEventRegistrationModal = ({
  open,
  onClose,
  event,
  participatingCompany,
}) => {
  const { t } = useTranslation();
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [qrError, setQrError] = useState(null);

  // Extract current appointment once using useMemo to avoid repeated access
  const currentAppointment = useMemo(() => {
    return participatingCompany?.citasAgendadas?.[0] || null;
  }, [participatingCompany]);

  // Memoize derived data to avoid recalculating on every render
  const requirementName = useMemo(() => {
    if (!currentAppointment || !participatingCompany?.requerimientos) {
      return "";
    }
    const requirement = participatingCompany.requerimientos.find(
      (req) => req._id === currentAppointment.idRequerimiento,
    );
    return requirement?.nombre || "";
  }, [currentAppointment, participatingCompany]);

  const eventDate = useMemo(() => {
    if (!currentAppointment?.fecha) {
      return "";
    }
    return dayjs(currentAppointment.fecha).utc().format("LL");
  }, [currentAppointment]);

  const schedule = useMemo(() => {
    if (!currentAppointment?.horaInicio || !currentAppointment?.horaFin) {
      return "";
    }
    return `${currentAppointment.horaInicio} - ${currentAppointment.horaFin}`;
  }, [currentAppointment]);

  const buyerExecutiveName = useMemo(() => {
    return currentAppointment?.nombreUsuarioComprador || "";
  }, [currentAppointment]);

  // Generate QR code
  useEffect(() => {
    const generateQR = async () => {
      // Reset error state
      setQrError(null);

      if (!event?._id || !currentAppointment) {
        setQrError("Missing required data for QR code");
        return;
      }

      try {
        const qrData = {
          userId: currentAppointment.idUsuarioRemitente,
          eventId: event._id,
          buyerCompanyId: currentAppointment.idEmpresaCompradora,
          appointmentId: currentAppointment._id,
        };

        const dataString = JSON.stringify(qrData);
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(dataString)}`;

        setQrCodeUrl(qrUrl);
      } catch (error) {
        logger.error("Failed to generate QR code:", {
          err: sanitizeError(error),
        });
        setQrError(t("errors.loading.qrCodeFailed"));
      }
    };

    generateQR();
  }, [event, currentAppointment]);

  // Early return if modal is not open
  if (!open) {
    return null;
  }

  // Early return if required data is missing
  if (!event || !participatingCompany || !currentAppointment) {
    return (
      <div className={styles.modalOverlay} onClick={onClose}>
        <div
          className={styles.modalContent}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <CloseIcon />
          </button>
          <div className={styles.modalHeader}>
            <h2>
              {t("components.viewRegistrationModal.errorTitle")}
            </h2>
          </div>
          <div className={styles.infoValue}>
            {t("components.viewRegistrationModal.missingData")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>

        <div className={styles.modalHeader}>
          <h2 id="modal-title">{event.nombre}</h2>
        </div>

        <div className={styles.infoContainer}>
          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <CalendarIcon className={styles.icon} />
              <span>{t("components.viewRegistrationModal.buyerCompany")}:</span>
            </div>
            <div className={styles.infoValue}>
              {participatingCompany.nombreComercial || "-"}
            </div>
          </div>

          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <CalendarIcon className={styles.icon} />
              <span>{t("components.viewRegistrationModal.eventDate")}:</span>
            </div>
            <div className={styles.infoValue}>{eventDate || "-"}</div>
          </div>

          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <ClockIcon className={styles.icon} />
              <span>
                {t("components.viewRegistrationModal.appointmentTime")}:
              </span>
            </div>
            <div className={styles.infoValue}>{schedule || "-"}</div>
          </div>
        </div>

        <div className={styles.infoContainer}>
          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <ClockIcon className={styles.icon} />
              <span>{t("components.viewRegistrationModal.requirement")}:</span>
            </div>
            <div className={styles.infoValue}>{requirementName || "-"}</div>
          </div>

          <div className={styles.infoBox}>
            <div className={styles.infoHeader}>
              <UserNameIcon className={styles.icon} />
              <span>
                {t("components.viewRegistrationModal.buyerExecutive")}:
              </span>
            </div>
            <div className={styles.infoValue}>{buyerExecutiveName || "-"}</div>
          </div>
        </div>

        <div className={styles.qrContainer}>
          {qrError ? (
            <div className={styles.infoValue} style={{ color: "#d32f2f" }}>
              {qrError}
            </div>
          ) : qrCodeUrl ? (
            <img
              src={qrCodeUrl}
              alt={t("common.qrCodeAlt")}
              className={styles.qrCode}
            />
          ) : (
            <div className={styles.infoValue}>
              {t("components.viewRegistrationModal.loadingQR") ||
                "Loading QR code..."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewBusinessEventRegistrationModal;
